import { Injectable } from '@angular/core';

import { HttpParams } from '@angular/common/http';
import { AnalysisApiClientAccess, AppFeatureService, AppRoleService, AppService } from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureKey,
  AppRoleKey,
  CompanySensitiveDataCompositeEntity,
  SensitiveDataCompanyAccessBase,
  SensitiveDataCompanyAccessEntity,
  SensitiveDataParams,
  SensitiveDataPersonAccessEntity,
  SensitiveDataTypeEntity,
  SensitiveDataTypeKey,
  SensitiveDataWatchlistAccessBase,
  SensitiveDataWatchlistAccessEntity,
  WatchlistSensitiveDataCompositeEntity,
} from '@nk/data-entities-lib';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class SensitiveDataService {
  private _loadTriggered = new BehaviorSubject(null);
  public readonly loadTriggered$ = this._loadTriggered.asObservable();

  constructor(
    private _analysisApiClient: AnalysisApiClientAccess,
    private _appFeatureService: AppFeatureService,
    private _cachingService: CachingService,
    private _appService: AppService,
    private _appRoleService: AppRoleService,
  ) {}

  public triggerSensitiveDataLoad(): void {
    this._loadTriggered.next(null);
  }

  public hasFeatureAndRoleAccess(appFeatureKey: AppFeatureKey = AppFeatureKey.payment_remark): Observable<boolean> {
    return combineLatest([
      this._appFeatureService.activeAppFeatureDimsCache.get(),
      this._appRoleService.activeAppRoleDimsCache.get(),
    ]).pipe(
      map(([featureDims, roleDims]) => {
        return featureDims.flags[appFeatureKey] && roleDims.flags[AppRoleKey.credit_check];
      }),
    );
  }

  public sensitiveDataWatchlistAccessEntityListCache = this._cachingService.create(
    this,
    this.sensitiveDataWatchlistAccessEntityList,
  );
  public sensitiveDataWatchlistAccessEntityList(): Observable<SensitiveDataWatchlistAccessEntity[]> {
    return combineLatest([
      this._appService.identityDataCache.get(),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check]),
    ]).pipe(
      switchMap(([identity, validRoleKeys]) => {
        let params = new HttpParams();
        params = params.append('app_customer_uuid', identity.app_customer.uuid);
        return this._analysisApiClient.get<SensitiveDataWatchlistAccessEntity[]>(
          `/sensitive-data-watchlist-access`,
          { params },
          undefined,
          validRoleKeys,
        );
      }),
    );
  }

  public sensitiveDataCompanyAccessEntityListCache = this._cachingService.create(
    this,
    this.sensitiveDataCompanyAccessEntityList,
  );
  public sensitiveDataCompanyAccessEntityList(): Observable<SensitiveDataCompanyAccessEntity[]> {
    return combineLatest([
      this._appService.identityDataCache.get(),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check]),
    ]).pipe(
      switchMap(([identity, validRoleKeys]) => {
        let params = new HttpParams();
        params = params.append('app_customer_uuid', identity.app_customer.uuid);
        return this._analysisApiClient.get<SensitiveDataCompanyAccessEntity[]>(
          `/sensitive-data-company-access`,
          { params },
          undefined,
          validRoleKeys,
        );
      }),
    );
  }

  public postSensitiveDataWatchlistAccessEntity(
    payload: SensitiveDataWatchlistAccessBase,
  ): Observable<SensitiveDataWatchlistAccessEntity> {
    let params = new HttpParams();
    params = params.set('app_customer_uuid', payload.app_customer_uuid);
    return this._appRoleService
      .claimValidAppRoleKeys([AppRoleKey.credit_check])
      .pipe(
        switchMap((validRoleKeys) =>
          this._analysisApiClient.post<SensitiveDataWatchlistAccessEntity>(
            `/sensitive-data-watchlist-access`,
            payload,
            { params },
            undefined,
            validRoleKeys,
          ),
        ),
      );
  }

  public postSensitiveDataCompanyAccessEntity(
    payload: SensitiveDataCompanyAccessBase,
  ): Observable<SensitiveDataCompanyAccessEntity> {
    let params = new HttpParams();
    params = params.set('app_customer_uuid', payload.app_customer_uuid);
    return this._appRoleService
      .claimValidAppRoleKeys([AppRoleKey.credit_check])
      .pipe(
        switchMap((validRoleKeys) =>
          this._analysisApiClient.post<SensitiveDataCompanyAccessEntity>(
            `/sensitive-data-company-access`,
            payload,
            { params },
            undefined,
            validRoleKeys,
          ),
        ),
      );
  }

  public deleteSensitiveDataWatchlistAccessEntity(
    sensitiveDataWatchlistAccessUuid: string,
    appCustomerUuid: string,
  ): Observable<SensitiveDataWatchlistAccessEntity> {
    let params = new HttpParams();
    params = params.set('app_customer_uuid', appCustomerUuid);
    return this._appRoleService
      .claimValidAppRoleKeys([AppRoleKey.credit_check])
      .pipe(
        switchMap((validRoleKeys) =>
          this._analysisApiClient.delete<SensitiveDataWatchlistAccessEntity>(
            `/sensitive-data-watchlist-access/${sensitiveDataWatchlistAccessUuid}`,
            { params },
            undefined,
            validRoleKeys,
          ),
        ),
      );
  }

  public deleteSensitiveDataCompanyAccessEntity(
    sensitiveDataCompanyAccessUuid: string,
    appCustomerUuid: string,
  ): Observable<SensitiveDataCompanyAccessEntity> {
    let params = new HttpParams();
    params = params.set('app_customer_uuid', appCustomerUuid);
    return this._appRoleService
      .claimValidAppRoleKeys([AppRoleKey.credit_check])
      .pipe(
        switchMap((validRoleKeys) =>
          this._analysisApiClient.delete<SensitiveDataCompanyAccessEntity>(
            `/sensitive-data-company-access/${sensitiveDataCompanyAccessUuid}`,
            { params },
            undefined,
            validRoleKeys,
          ),
        ),
      );
  }

  public sensitiveDataTypeListCache = this._cachingService.create(this, this.fetchSensitiveDataTypeList);
  public fetchSensitiveDataTypeList(): Observable<SensitiveDataTypeEntity[]> {
    return this._analysisApiClient.get<SensitiveDataTypeEntity[]>(`/sensitive-data-type`);
  }

  public sensitiveDataCompositeByParamsCache = this._cachingService.create(
    this,
    this.fetchSensitiveDataCompositeByParams,
  );
  public fetchSensitiveDataCompositeByParams(
    appCustomerUuid: string,
    params: SensitiveDataParams,
  ): Observable<CompanySensitiveDataCompositeEntity[] | WatchlistSensitiveDataCompositeEntity[]> {
    let httpParams = new HttpParams();
    if ('companyEventUuid' in params) {
      httpParams = httpParams.set('company_event_uuid', params.companyEventUuid);
    } else if ('eventUuid' in params) {
      httpParams = httpParams.set('event_uuid', params.eventUuid);
    } else if ('companyIdentifier' in params) {
      httpParams = httpParams.set('company_identifier', params.companyIdentifier);
    } else if ('watchlistUuid' in params) {
      httpParams = httpParams.set('watchlist_uuid', params.watchlistUuid);
    } else {
      throw new Error('Missing either company, watchlist, event or company event for sensitive data check');
    }
    httpParams = httpParams.set('app_customer_uuid', appCustomerUuid);
    if ('watchlistUuid' in params) {
      return this._appRoleService
        .claimValidAppRoleKeys([AppRoleKey.credit_check])
        .pipe(
          switchMap((validRoleKeys) =>
            this._analysisApiClient.get<WatchlistSensitiveDataCompositeEntity[]>(
              `/sensitive-data-watchlist-access-composite`,
              { params: httpParams },
              undefined,
              validRoleKeys,
            ),
          ),
        );
    } else {
      return this._appRoleService
        .claimValidAppRoleKeys([AppRoleKey.credit_check])
        .pipe(
          switchMap((validRoleKeys) =>
            this._analysisApiClient.get<CompanySensitiveDataCompositeEntity[]>(
              `/company-sensitive-data-composite`,
              { params: httpParams },
              undefined,
              validRoleKeys,
            ),
          ),
        );
    }
  }

  public companySensitiveDataCompositeListCache = this._cachingService.create(
    this,
    this.fetchCompanySensitiveDataCompositeList,
  );
  public fetchCompanySensitiveDataCompositeList(
    appCustomerUuid: string,
    companyUuid?: string,
  ): Observable<CompanySensitiveDataCompositeEntity[]> {
    let params = new HttpParams();
    params = params.set('app_customer_uuid', appCustomerUuid);
    if (companyUuid) {
      params = params.set('company_identifier', companyUuid);
    }
    return this._appRoleService
      .claimValidAppRoleKeys([AppRoleKey.credit_check])
      .pipe(
        switchMap((validRoleKeys) =>
          this._analysisApiClient.get<CompanySensitiveDataCompositeEntity[]>(
            `/company-sensitive-data-composite`,
            { params },
            undefined,
            validRoleKeys,
          ),
        ),
      );
  }

  public watchlistSensitiveDataCompositeListCache = this._cachingService.create(
    this,
    this.fetchWatchlistSensitiveDataCompositeList,
  );
  public fetchWatchlistSensitiveDataCompositeList(
    appCustomerUuid: string,
    appWatchlistUuid?: string,
  ): Observable<WatchlistSensitiveDataCompositeEntity[]> {
    let params = new HttpParams();
    params = params.set('app_customer_uuid', appCustomerUuid);
    if (appWatchlistUuid) {
      params = params.set('watchlist_uuid', appWatchlistUuid);
    }
    return this._appRoleService
      .claimValidAppRoleKeys([AppRoleKey.credit_check])
      .pipe(
        switchMap((validRoleKeys) =>
          this._analysisApiClient.get<WatchlistSensitiveDataCompositeEntity[]>(
            `/sensitive-data-watchlist-access-composite`,
            { params },
            undefined,
            validRoleKeys,
          ),
        ),
      );
  }

  public fetchSensitiveDataTypesByWatchlist(watchlistUuid: string): Observable<SensitiveDataTypeEntity[]> {
    return this._appService.identityDataCache.get().pipe(
      switchMap((identity) =>
        this.watchlistSensitiveDataCompositeListCache.get(identity.app_customer.uuid, watchlistUuid).pipe(
          map((composites) => {
            return composites.length ? composites[0].sensitive_data_types : [];
          }),
        ),
      ),
    );
  }

  public claimValidSensitiveDataTypeKeysByParams(
    params: SensitiveDataParams,
    sensitiveDataTypeKeys: SensitiveDataTypeKey[],
  ): Observable<SensitiveDataTypeKey[]> {
    return this._appService.identityDataCache.get().pipe(
      switchMap((identity) => {
        return this.sensitiveDataCompositeByParamsCache.get(identity.app_customer.uuid, params).pipe(
          map((sensitiveDataTypes) => {
            if (sensitiveDataTypes && sensitiveDataTypes.length) {
              const sensitiveDataType = sensitiveDataTypes[0];
              return sensitiveDataType.sensitive_data_types
                .map((sdt) => sdt.sensitive_data_type_key as SensitiveDataTypeKey)
                .filter((sdt) => sensitiveDataTypeKeys.includes(sdt));
            } else {
              return [] as SensitiveDataTypeKey[];
            }
          }),
        );
      }),
    );
  }

  public fetchSensitiveDataTypesByParams(params: SensitiveDataParams): Observable<SensitiveDataTypeEntity[]> {
    return this._appService.identityDataCache.get().pipe(
      switchMap((identity) =>
        this.sensitiveDataCompositeByParamsCache.get(identity.app_customer.uuid, params).pipe(
          map((composites) => {
            if (composites.length > 0) {
              if (composites[0].sensitive_data_types) {
                return composites[0].sensitive_data_types;
              }
            }
            return [];
          }),
        ),
      ),
    );
  }

  public fetchSensitiveDataTypesByCompany(companyIdentifier: string): Observable<SensitiveDataTypeEntity[]> {
    return this._appService.identityDataCache.get().pipe(
      switchMap((identity) =>
        this.companySensitiveDataCompositeListCache.get(identity.app_customer.uuid, companyIdentifier).pipe(
          map((composites) => {
            if (composites.length > 0) {
              return composites[0].sensitive_data_types;
            } else {
              return [];
            }
          }),
        ),
      ),
    );
  }

  public claimValidSensitiveDataTypeKeysByWatchlist(
    watchlistUuid: string,
    sensitiveDataTypeKeys: SensitiveDataTypeKey[],
  ): Observable<SensitiveDataTypeKey[]> {
    return this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check]).pipe(
      switchMap((validRoles) => {
        if (validRoles.includes(AppRoleKey.credit_check)) {
          return this.fetchSensitiveDataTypesByWatchlist(watchlistUuid).pipe(
            map((sensitiveDataTypes) => {
              if (sensitiveDataTypes) {
                return sensitiveDataTypes
                  .map((sdt) => sdt.sensitive_data_type_key as SensitiveDataTypeKey)
                  .filter((sdt) => sensitiveDataTypeKeys.includes(sdt));
              } else {
                return [];
              }
            }),
          );
        } else {
          return of<SensitiveDataTypeKey[]>([]);
        }
      }),
    );
  }

  public claimValidSensitiveDataTypeKeysByCompany(
    companyIdentifier: string,
    sensitiveDataTypeKeys: SensitiveDataTypeKey[],
  ): Observable<SensitiveDataTypeKey[]> {
    return this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check]).pipe(
      switchMap((validRoles) => {
        if (validRoles.includes(AppRoleKey.credit_check)) {
          return this.fetchSensitiveDataTypesByCompany(companyIdentifier).pipe(
            map((sensitiveDataTypes) =>
              sensitiveDataTypes
                .map((sdt) => sdt.sensitive_data_type_key as SensitiveDataTypeKey)
                .filter((sdt) => sensitiveDataTypeKeys.includes(sdt)),
            ),
          );
        } else {
          return of<SensitiveDataTypeKey[]>([]);
        }
      }),
    );
  }

  public sensitiveDataPersonAccessCache = this._cachingService.create(this, this.fetchSensitiveDataPersonAccess);
  public fetchSensitiveDataPersonAccess(
    personUuid: string,
    appMembershipUuid: string,
    appCustomerUuid: string,
  ): Observable<SensitiveDataPersonAccessEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('app_membership_uuid', appMembershipUuid);
    httpParams = httpParams.append('app_customer_uuid', appCustomerUuid);
    return this._analysisApiClient.get<SensitiveDataPersonAccessEntity[]>(
      `/person/${personUuid}/sensitive-data-person-access`,
      { params: httpParams },
      [AppFeatureKey.base],
      [AppRoleKey.person_national_identification],
    );
  }

  public postSensitiveDataPersonAccess(
    personUuid: string,
    appMembershipUuid: string,
    appCustomerUuid: string,
    sensitiveDataTypeUuid: string,
  ): Observable<SensitiveDataPersonAccessEntity[]> {
    // TODO: clear cache for the one above
    const payload = {
      app_customer_uuid: appCustomerUuid,
      app_membership_uuid: appMembershipUuid,
      sensitive_data_type_uuid: sensitiveDataTypeUuid,
      person_uuid: personUuid,
    };
    return this._analysisApiClient.post<SensitiveDataPersonAccessEntity[]>(
      `/sensitive-data-person-access`,
      payload,
      {},
      [AppFeatureKey.base],
      [AppRoleKey.person_national_identification],
    );
  }
}
